import React, { Suspense, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaReact, FaNodeJs, FaNpm, FaGithub, FaCode, FaDatabase, FaServer, FaTools, FaArrowRight, } from 'react-icons/fa';
import { SiMongodb, SiExpress, SiAmazonaws, SiGit, SiFirebase  } from 'react-icons/si';
import Testimonials from '../components/Testimonials';
import ChatBot from '../components/ChatBot/ChatBot';
import SEO from '../components/SEO/SEO';
import Banner from '../components/OfferBanner'
import Projects from '../components/Projects'


const ThreeBackground = React.lazy(() => import('../components/ThreeBackground'));


const Home = () => {
  const [showAllServices, setShowAllServices] = useState(false);

  const technologies = [
    { icon: <SiMongodb />, name: 'MongoDB', color: 'text-green-500', level: 85 },
    { icon: <SiExpress />, name: 'Express', color: 'text-gray-400', level: 85 },
    { icon: <FaReact />, name: 'React', color: 'text-blue-400', level: 90 },
    { icon: <FaNodeJs />, name: 'Node.js', color: 'text-green-600', level: 85 },
  ];

  const services = [
    { title: 'Full-Stack Web Development', icon: <FaCode />, description: 'Dynamic and responsive web applications using MERN Stack' },
    { title: 'API Development', icon: <FaServer />, description: 'Scalable RESTful with authentication and security' },
    { title: 'Database Design & Optimization', icon: <FaDatabase />, description: 'Efficient NoSQL database architectures for performance' },
    { title: 'Deployment', icon: <FaTools />, description: 'CI/CD pipelines, cPanel, Docker, and cloud server management' },
    { title: 'Cloud Services & Hosting', icon: <SiAmazonaws />, description: 'AWS, Firebase, and secure cloud-based solutions' },
    { title: 'Version Control & Collaboration', icon: <SiGit />, description: 'Git, GitHub, and workflow management for seamless development' },
    { title: 'Real-Time & Interactive Features', icon: <SiFirebase />, description: 'Live chat, notifications, and WebSocket-based real-time updates' }
  ];
  

  return (
    <>
      <SEO 
        title="Mahmudur Rahman - Full Stack Developer"
        description="Full Stack Developer specializing in MERN stack development. Building modern web applications with React, Node.js, and MongoDB."
        image="https://res.cloudinary.com/dqovjmmlx/image/upload/v1740488530/portfolio-banner_xywidc.jpg"
        url="https://devplus.fun"
        keywords="Full Stack Developer, MERN Stack, React, Node.js, MongoDB, Web Development, JavaScript, Next.js, Express.js"
        author="Mahmudur Rahman"
      />

      
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white overflow-hidden">
        <Banner />
        {/* 3D Background */}
          <Suspense fallback={<div className="absolute inset-0 z-0 bg-gray-900" />}>
            <div className="absolute inset-0 z-0">
                <ThreeBackground />
              </div>
          </Suspense>

        {/* Hero Section */}
        <div className="relative z-10 container mx-auto mt-12 px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl place-content-center mx-auto h-[60vh]"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-center justify-items-center text-transparent">
              MERN Stack Developer
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Building modern web applications with cutting-edge technologies
            </p>
            <div className="flex justify-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                className="px-8 py-3 bg-blue-600 rounded-full font-medium hover:bg-blue-700 transition-colors"
              >
                <Link to="/projects">View Projects</Link>
              
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                className="px-8 py-3 border-2 border-blue-600 rounded-full font-medium hover:bg-blue-600/20 transition-colors"
              >
                <Link to="/contact">Contact</Link>
              </motion.button>
            </div>
          </motion.div>
         

          {/* Tech Stack Section with Link */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-20"
          >
            <div className="flex justify-between items-center mb-10">
              <h2 className="text-3xl font-bold">Tech Stack</h2>
              <Link to="/skills" className="group flex items-center space-x-2 text-blue-400 hover:text-blue-300">
                <span>View All Skills</span>
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <FaArrowRight />
                </motion.div>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {technologies.map((tech, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/5 backdrop-blur-lg p-6 rounded-xl relative overflow-hidden group"
                >
                  <div className={`text-4xl ${tech.color} mb-3 flex justify-center`}>
                    {tech.icon}
                  </div>
                  <div className="text-gray-300 text-center">{tech.name}</div>
                  <motion.div
                    className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-600"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${tech.level}%` }}
                    transition={{ duration: 1 }}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Services Section with Show More */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-20"
          >
            <h2 className="text-3xl font-bold text-center mb-10">Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {services.slice(0, showAllServices ? services.length : 3).map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    whileHover={{ scale: 1.05 }}
                    className="bg-white/5 backdrop-blur-lg p-6 rounded-xl"
                  >
                    <Link to="/skills" >
                    <div className="text-3xl text-blue-400 mb-4">{service.icon}</div>
                    <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                    <p className="text-gray-400">{service.description}</p>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            
            <motion.button
              onClick={() => setShowAllServices(!showAllServices)}
              className="mt-8 mx-auto block px-6 py-3 border border-blue-500 rounded-full hover:bg-blue-500/20 transition-colors"
            >
              {showAllServices ? 'Show Less' : 'Show More'}
            </motion.button>
          </motion.div>

          {/* Animated Stats Section */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {[
              { label: "Projects", value: "37+" },
              { label: "Clients", value: "21+" },
              { label: "Experience", value: "2+ Years" },
              { label: "Technologies", value: "10+" },

            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-lg p-6 rounded-xl text-center"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"
                >
                  {stat.value}
                </motion.div>
                <div className="text-gray-400 mt-2">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

            <Projects limit={3} />
            <div className="text-center mt-12">
              <Link to="/projects">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-full font-medium transition-colors duration-300"
                >
                  View All Projects
                </motion.button>
              </Link>
            </div>
          <Testimonials />

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full text-lg font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
            >
              Start a Project
            </Link>
          </motion.div>

          {/* GitHub Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-20 text-center"
          >
            <Link
              to="/projects"
              className="inline-flex items-center space-x-2 text-gray-300 hover:text-white"
            >
              <FaGithub className="text-2xl" />
              <span>Check out my open source contributions</span>
            </Link>
            <br></br>
            <Link
              to="https://www.npmjs.com/~conflow"
              target='_blank'
              className="inline-flex items-center space-x-2 text-gray-300 hover:text-white"
            >
              <FaNpm className="text-2xl" />
              <span>npm Packages</span>
            </Link>
          </motion.div>
          <ChatBot />
        </div>
      </div>
    </>
  );
};

export default Home;
